import * as React from 'react';
import { styled } from '@linaria/react';

import useTickSfx from '@/hooks/use-tick-sfx';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Space from '@/components/Space';

import {
  POSITION_RADIUS,
  Svg,
  Label,
  PositionDot,
  CodeSnippet,
  Indicators,
  Row,
} from './BasicShapes.shared';
import GuideLine from './GuideLine';
import SizeLine from './SizeLine';

const SIZE = 300;

function BasicEllipseDemo() {
  const id = React.useId().replace(/:/g, '');
  const maskId = `${id}-ellipse-mask`;

  const playTick = useTickSfx();

  return (
    <Demo
      id="basic-ellipse-demo"
      initialValues={{
        cx: Math.round(SIZE / 2),
        cy: Math.round(SIZE / 2),
        rx: Math.round(SIZE / 4),
        ry: Math.round(SIZE / 6),
      }}
      outputWrapper={OutputWrapper}
      CustomGridComponent={ControlsGrid}
      controls={[
        <DemoSliderControl
          highlightControlWhenActive
          key="cx"
          id="cx"
          label="cx"
          secondaryLabel="(center x)"
          min={0}
          max={SIZE}
          step={10}
          wrapperStyle={{
            gridArea: 'cx',
          }}
          onChange={() => {
            playTick();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="cy"
          id="cy"
          label="cy"
          secondaryLabel="(center y)"
          min={0}
          max={SIZE}
          step={10}
          wrapperStyle={{
            gridArea: 'cy',
          }}
          onChange={() => {
            playTick();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="rx"
          id="rx"
          label="rx"
          secondaryLabel="(horizontal radius)"
          min={0}
          max={Math.round(SIZE * 0.5)}
          step={2}
          wrapperStyle={{
            gridArea: 'rx',
          }}
          onChange={() => {
            playTick();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="ry"
          id="ry"
          label="ry"
          secondaryLabel="(vertical radius)"
          min={0}
          max={Math.round(SIZE * 0.5)}
          step={2}
          wrapperStyle={{
            gridArea: 'ry',
          }}
          onChange={() => {
            playTick();
          }}
        />,
      ]}
    >
      {({ cx, cy, rx, ry }, activeControlId) => {
        const code = `\
<svg width="${SIZE}" height="${SIZE}">
  <ellipse
    cx="${cx}"
    cy="${cy}"
    rx="${rx}"
    ry="${ry}"
    fill="none"
    stroke="oklch(0.9 0.3 164)"
    stroke-width="5"
  />
</svg>\
`;

        return (
          <StyledRow>
            <StyledCodeSnippet code={code} />

            <SvgWrapper>
              <Svg
                size={SIZE}
                defs={
                  <mask id={maskId}>
                    <rect width={SIZE} height={SIZE} fill="black" />
                    <ellipse
                      cx={cx}
                      cy={cy}
                      rx={Math.max(rx - 5, 0)}
                      ry={Math.max(ry - 5, 0)}
                      fill="white"
                    />
                  </mask>
                }
              >
                <PositionDot
                  cx={cx}
                  cy={cy}
                  mask={`url(#${maskId})`}
                />

                {/* MAIN SHAPE: */}
                <ellipse
                  cx={cx}
                  cy={cy}
                  rx={rx}
                  ry={ry}
                  fill="transparent"
                  stroke="var(--wham-complementary-500)"
                  strokeWidth={5}
                />

                <Indicators
                  style={{
                    opacity: rx === 0 || ry === 0 ? 0 : 1,
                  }}
                >
                  {/* Guidelines — visible white lines */}
                  {/* cx line */}
                  <GuideLine
                    x1={cx - POSITION_RADIUS - 2}
                    y1={cy}
                    x2={0}
                    y2={cy}
                    isActive={activeControlId === 'cx'}
                  />
                  {/* cy line */}
                  <GuideLine
                    x1={cx}
                    y1={cy - POSITION_RADIUS - 2}
                    x2={cx}
                    y2={0}
                    isActive={activeControlId === 'cy'}
                  />
                  {/* rx line */}
                  <SizeLine
                    x1={cx + 8}
                    y1={cy}
                    x2={cx + rx - 6}
                    y2={cy}
                    isActive={activeControlId === 'rx'}
                    isHidden={rx <= 13}
                    mask={`url(#${maskId})`}
                  />
                  {/* ry line */}
                  <SizeLine
                    x1={cx}
                    y1={cy + 8}
                    x2={cx}
                    y2={cy + ry - 6}
                    isActive={activeControlId === 'ry'}
                    isHidden={ry <= 13}
                    mask={`url(#${maskId})`}
                  />

                  <Label
                    x={cx / 2}
                    y={cy + 16}
                    textAnchor="middle"
                    dominantBaseline="bottom"
                    data-is-hidden={cx < 30}
                    data-is-active={activeControlId === 'cx'}
                    style={{
                      fontSize: '0.85rem',
                    }}
                  >
                    cx
                  </Label>
                  <Label
                    x={cx + 30}
                    y={cy / 2}
                    textAnchor="end"
                    dominantBaseline="middle"
                    data-is-hidden={cy < 20}
                    data-is-active={activeControlId === 'cy'}
                    style={{
                      fontSize: '0.85rem',
                    }}
                  >
                    cy
                  </Label>
                  <Label
                    x={Math.max(cx + rx / 2, cx + 32)}
                    y={cy + 16}
                    textAnchor="middle"
                    dominantBaseline="bottom"
                    data-is-active={activeControlId === 'rx'}
                    style={{
                      fontSize: '0.85rem',
                    }}
                    mask={`url(#${maskId})`}
                  >
                    rx
                  </Label>
                  <Label
                    x={cx + 18}
                    y={Math.max(cy + ry / 2, cy + 32)}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    data-is-active={activeControlId === 'ry'}
                    style={{
                      fontSize: '0.85rem',
                    }}
                    mask={`url(#${maskId})`}
                  >
                    ry
                  </Label>
                </Indicators>
              </Svg>
            </SvgWrapper>
          </StyledRow>
        );
      }}
    </Demo>
  );
}

const OutputWrapper = styled(Space)`
  container-type: inline-size;
  background: var(--color-background);
`;

const ControlsGrid = styled.div`
  display: grid;
  gap: 16px 32px;
  grid-template-areas:
    'cx'
    'cy'
    'rx'
    'ry';

  @media (min-width: 30rem) {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'cx cy'
      'rx ry';
  }
`;

const StyledRow = styled(Row)`
  @container (max-width: 37rem) {
    flex-direction: column;
    align-items: center;
  }
`;

const StyledCodeSnippet = styled(CodeSnippet)`
  @container (max-width: 37rem) {
    width: 100%;
  }
`;

const SvgWrapper = styled.div`
  display: flex;

  @container (max-width: 37rem) {
    position: relative;
  }
`;

export default BasicEllipseDemo;
